import {
  edgeViewSchema,
  fleetSummarySchema,
  nodeMetricSeriesSchema,
  nodeViewSchema,
  type EdgeView,
  type FleetSummary,
  type NodeMetricSeries,
  type NodeView,
} from '@kraaapf/contracts';
import { z } from 'zod';

import type { PortalClient } from '../http-client';

/**
 * Die Chronik eines Nodes.
 *
 * Nicht im Vertrag, weil sie nur der Master und diese Oberfläche kennen — der
 * Agent hat mit ihr nichts zu tun. Dieselbe Regel wie umgekehrt: Was zwei
 * Prozesse teilen, steht im Vertrag; was einer ausgibt, steht an seiner
 * Schnittstelle.
 */
const nodeEventSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  type: z.string(),
  previousStatus: z.string().nullable(),
  currentStatus: z.string().nullable(),
  reason: z.string(),
});

export type NodeEvent = z.infer<typeof nodeEventSchema>;

/**
 * Das Bootstrap-Token eines Nodes oder einer Edge — einmalig sichtbar.
 *
 * `hint` kommt vom Master mit und steht so, wie er ihn formuliert hat, in der
 * Oberfläche: Bei einer Edge nennt er zusätzlich den DNS-Eintrag, der vorher
 * stehen muss. Ihn hier nachzubauen hieße, dieselbe Auskunft zweimal zu
 * pflegen.
 */
const bootstrapTokenSchema = z.object({
  bootstrapToken: z.string(),
  expiresAt: z.string(),
  hint: z.string(),
});

/** Flotte: Nodes und Edges. */
function buildFleetApi(client: PortalClient) {
  return {
    async summary(): Promise<FleetSummary> {
      return client.get('/fleet/summary', fleetSummarySchema);
    },

    async nodes(): Promise<NodeView[]> {
      return client.get('/fleet/nodes', z.array(nodeViewSchema));
    },

    async node(nodeId: string): Promise<NodeView> {
      return client.get(`/fleet/nodes/${nodeId}`, nodeViewSchema);
    },

    async nodeEvents(nodeId: string, limit = 50): Promise<NodeEvent[]> {
      return client.get(`/fleet/nodes/${nodeId}/events`, z.array(nodeEventSchema), {
        query: { limit },
      });
    },

    /**
     * Die Messwert-Zeitreihe eines Nodes.
     *
     * `sinceMinutes` bestimmt den Ausschnitt, `points` die gewünschte
     * Auflösung. Die tatsächliche Eimerbreite steht in der Antwort — ohne sie
     * ließe sich eine Lücke in den Daten nicht von einem stillen Node
     * unterscheiden.
     */
    async nodeMetrics(
      nodeId: string,
      options: { sinceMinutes?: number; points?: number } = {},
    ): Promise<NodeMetricSeries> {
      return client.get(`/fleet/nodes/${nodeId}/metrics`, nodeMetricSeriesSchema, {
        query: { sinceMinutes: options.sinceMinutes, points: options.points },
      });
    },

    async createNode(input: {
      note?: string;
      region?: string;
      labels?: Record<string, string>;
    }): Promise<NodeView> {
      return client.post('/fleet/nodes', nodeViewSchema, input);
    },

    async issueNodeBootstrapToken(nodeId: string) {
      return client.post(`/fleet/nodes/${nodeId}/bootstrap-token`, bootstrapTokenSchema);
    },

    async drainNode(nodeId: string, reason: string): Promise<NodeView> {
      return client.post(`/fleet/nodes/${nodeId}/drain`, nodeViewSchema, { reason });
    },

    async setNodeMaintenance(nodeId: string, reason: string): Promise<NodeView> {
      return client.post(`/fleet/nodes/${nodeId}/maintenance`, nodeViewSchema, { reason });
    },

    async resumeNode(nodeId: string): Promise<NodeView> {
      return client.post(`/fleet/nodes/${nodeId}/resume`, nodeViewSchema);
    },

    async removeNode(nodeId: string): Promise<void> {
      await client.send('DELETE', `/fleet/nodes/${nodeId}`);
    },

    // -- Edges ---------------------------------------------------------------

    async edges(): Promise<EdgeView[]> {
      return client.get('/fleet/edges', z.array(edgeViewSchema));
    },

    async edge(edgeId: string): Promise<EdgeView> {
      return client.get(`/fleet/edges/${edgeId}`, edgeViewSchema);
    },

    async createEdge(input: { note?: string }): Promise<EdgeView> {
      return client.post('/fleet/edges', edgeViewSchema, input);
    },

    async issueEdgeBootstrapToken(edgeId: string) {
      return client.post(`/fleet/edges/${edgeId}/bootstrap-token`, bootstrapTokenSchema);
    },

    async removeEdge(edgeId: string): Promise<void> {
      await client.send('DELETE', `/fleet/edges/${edgeId}`);
    },
  };
}

/** Die öffentliche Form dieses Bereichs — abgeleitet, damit sie nicht zweimal dasteht. */
export type FleetApi = ReturnType<typeof buildFleetApi>;

export function createFleetApi(client: PortalClient): FleetApi {
  return buildFleetApi(client);
}
